/**
 * Unified App Notification System
 * 
 * Provides a single entry point for all in-app notifications across Web/PWA/Electron.
 * In-app notifications are PRIMARY, OS notifications are SECONDARY.
 * 
 * Design:
 * - Maps event.severity to UI behavior (toast/banner/modal)
 * - Works identically across all platforms
 * - OS notifications are optional fallback
 * 
 * Phase 2 Severity Mapping:
 * - info: Toast (auto-dismiss)
 * - warning: Sticky banner (manual dismiss)
 * - critical: Modal (requires acknowledgment)
 */

import { toast } from 'react-toastify';
import notificationStore from '../stores/notificationStore';
import { claimDesktopNotification } from './desktopNotificationClaim';

// Track seen event IDs to prevent duplicates
const seenEventIds = new Set();

/**
 * Emit an in-app notification (PRIMARY notification channel)
 * 
 * @param {Object} event - Canonical event object
 * @param {string} event.id - Event ID (for deduplication)
 * @param {string} event.severity - Severity: info|warning|error|critical
 * @param {string} event.source - Event source (sip|sms|webhook|flow|action|device|system)
 * @param {string} event.type - Event type (inbound|outbound|status|error)
 * @param {Object} event.context - Event context with route, status, meta
 * @param {Object} options - Additional options
 * @param {boolean} options.skipOSNotification - Skip OS notification (default: false)
 * @param {boolean} options.skipInApp - Skip store + stack card (use when a dedicated UI already shows)
 * @param {Function} options.onOSNotificationAttempt - Callback for OS notification attempt
 * @returns {Promise<Object>} Result with inApp and os status
 */
export async function emitAppNotification(event, options = {}) {
  const eventId = event?.id || event?.eventId;
  const severity = event?.severity || 'info';
  const source = event?.source || 'system';
  const type = event?.type || 'notification';
  const context = event?.context || {};
  
  // Log notification emission
  try {
    console.log(
      '[APP-NOTIFY] emitAppNotification called ' +
        JSON.stringify({
          eventId,
          severity,
          source,
          type,
          hasContext: Boolean(context),
          skipOS: options.skipOSNotification,
          skipInApp: Boolean(options.skipInApp),
        })
    );
  } catch (_) {}

  // Deduplication check
  if (eventId && seenEventIds.has(String(eventId))) {
    try {
      console.log('[APP-NOTIFY] SKIP duplicate eventId=' + eventId);
    } catch (_) {}
    return { inApp: { skipped: true, reason: 'duplicate' }, os: null };
  }

  // Mark as seen
  if (eventId) {
    seenEventIds.add(String(eventId));
    // Limit set size to prevent memory leak
    if (seenEventIds.size > 1000) {
      const arr = Array.from(seenEventIds);
      seenEventIds.clear();
      arr.slice(-500).forEach(id => seenEventIds.add(id));
    }
  }

  const title = buildNotificationTitle(event);
  const body = buildNotificationBody(event);

  if (options.skipInApp) {
    let osResult = null;
    if (!options.skipOSNotification) {
      osResult = await attemptOSNotification({
        title,
        body,
        event,
        onAttempt: options.onOSNotificationAttempt,
      });
    }
    return { inApp: { skipped: true, reason: 'skip_in_app' }, os: osResult };
  }

  // Add to notification store (for history and state tracking)
  const storedNotification = notificationStore.addNotification(event);
  if (!storedNotification) {
    // Already exists in store
    return { inApp: { skipped: true, reason: 'duplicate_in_store' }, os: null };
  }

  // Render in-app notification based on severity
  const inAppResult = await renderInAppNotification({
    severity,
    title,
    body,
    event,
    notification: storedNotification
  });

  try {
    console.log(
      '[APP-NOTIFY] in-app notification rendered ' +
        JSON.stringify({
          severity,
          type: inAppResult.type,
          title: title.substring(0, 50)
        })
    );
  } catch (_) {}

  // Play alert audio if alertMeta.audio is true (Device Alert feature)
  if (event?.alertMeta?.audio) {
    try {
      playAlertTone();
      console.log('[APP-NOTIFY] alert audio played');
    } catch (audioErr) {
      console.warn('[APP-NOTIFY] alert audio failed (non-blocking):', audioErr);
    }
  }

  // Optionally trigger OS notification (SECONDARY)
  let osResult = null;
  if (!options.skipOSNotification) {
    osResult = await attemptOSNotification({
      title,
      body,
      event,
      onAttempt: options.onOSNotificationAttempt
    });
  }

  return {
    inApp: inAppResult,
    os: osResult
  };
}

/**
 * Build notification title from event
 */
function buildNotificationTitle(event) {
  const explicit =
    event?.context?.meta?.title ||
    event?.context?.title ||
    event?.title;
  if (explicit) return String(explicit);

  const source = event?.source || 'Event';
  const type = event?.type || 'Update';
  const severity = event?.severity || 'info';

  if (severity === 'critical' || severity === 'error') {
    return `${severity.toUpperCase()}: ${source} / ${type}`;
  }

  return `${source} / ${type}`;
}

/**
 * Build notification body from event context
 */
function buildNotificationBody(event) {
  const context = event?.context || {};
  const explicit = context.meta?.message || context.message || event?.message;
  if (explicit) return String(explicit);

  const parts = [];
  if (context.route) parts.push(`Route: ${context.route}`);
  if (context.status) parts.push(`Status: ${context.status}`);
  return parts.join(' • ') || 'New event received';
}

/**
 * Render in-app notification based on severity
 * 
 * Phase 2 Severity mapping:
 * - info: toast (auto-dismiss 5s)
 * - warning: sticky banner (manual dismiss) - triggers via store update
 * - error: toast (auto-dismiss 10s, error style)
 * - critical: modal (requires ack) - triggers via store update + window focus
 */
async function renderInAppNotification({ severity, title, body, event, notification }) {
  const message = `${title}\n${body}`;

  // ALL push notifications are rendered via NotificationResponseStack /
  // NotificationResponseModal. Notifications with response buttons show
  // action buttons; those without show just a close button.
  // The modal is driven by notificationStore — no extra wiring needed here.

  // Focus window for high/emergency notifications
  if ((severity === 'critical' || severity === 'error') &&
      typeof window !== 'undefined' && window.electronAPI?.focusWindow) {
    try {
      await window.electronAPI.focusWindow();
      console.log('[APP-NOTIFY] focused window for notification');
    } catch (e) {
      console.warn('[APP-NOTIFY] failed to focus window:', e);
    }
  }

  const hasResponses = notification?.responses && notification.responses.length > 0;
  console.log('[APP-NOTIFY] notification → response modal (hasResponses=' + hasResponses + ', severity=' + severity + ')');

  return {
    type: 'response_modal',
    severity,
    autoClose: false,
  };
}

/**
 * Attempt OS notification (SECONDARY channel)
 * Only fires AFTER in-app notification is rendered
 * Failure does not block in-app UI
 */
async function attemptOSNotification({ title, body, event, onAttempt }) {
  const visibility = getNotificationVisibility(event);
  const visibleAndFocused = isAppVisibleAndFocused();

  if (visibility === 'in_app_only') {
    try {
      console.log('[APP-NOTIFY] skipping OS notification (visibility=in_app_only)');
    } catch (_) {}
    return { attempted: false, reason: 'in_app_only' };
  }

  if (visibility === 'when_minimized' && visibleAndFocused) {
    try {
      console.log('[APP-NOTIFY] skipping OS notification (app is visible and focused)');
    } catch (_) {}
    return { attempted: false, reason: 'visible_and_focused' };
  }

  try {
    console.log(
      '[APP-NOTIFY] attempting OS notification (secondary) ' +
        JSON.stringify({ visibility, visibleAndFocused })
    );
  } catch (_) {}

  if (onAttempt) {
    onAttempt({ title, body, event });
  }

  const isElectron = typeof window !== 'undefined' && !!window.electronAPI?.showNotification;
  if (isElectron) {
    try {
      const result = await window.electronAPI.showNotification({
        title,
        body: body || 'New event received',
      });
      console.log('[APP-NOTIFY] OS notification result: ' + JSON.stringify(result || null));
      return {
        attempted: true,
        success: result?.success || false,
        result,
      };
    } catch (e) {
      console.warn(
        '[APP-NOTIFY] OS notification failed (non-blocking): ' +
          JSON.stringify({
            message: e?.message,
            stack: e?.stack?.substring(0, 200),
          })
      );
      return {
        attempted: true,
        success: false,
        error: e?.message,
      };
    }
  }

  const tag = event?.id || event?.eventId || 'signalbridge-reminder';
  return attemptWebNotification({ title, body, tag });
}

async function attemptWebNotification({ title, body, tag }) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return { attempted: false, reason: 'no_notification_api' };
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch (_) {
      return { attempted: false, reason: 'permission_request_failed' };
    }
  }
  if (permission !== 'granted') {
    return { attempted: false, reason: 'permission_denied' };
  }

  const notificationTag = tag || 'signalbridge-reminder';
  const claimed = await claimDesktopNotification(notificationTag);
  if (!claimed) {
    return { attempted: false, reason: 'shown_in_another_tab' };
  }

  try {
    const notification = new Notification(title || 'Reminder', {
      body: body || 'Reminder',
      tag: notificationTag,
    });
    return { attempted: true, success: true, result: { title: notification.title } };
  } catch (e) {
    return { attempted: true, success: false, error: e?.message };
  }
}

function getNotificationVisibility(event) {
  const rawVisibility =
    event?.visibility ||
    event?.context?.meta?.visibility ||
    event?.alertMeta?.visibility ||
    'in_app_only';

  const normalized = String(rawVisibility).trim().toLowerCase();
  if (
    normalized === 'in_app_only' ||
    normalized === 'when_minimized' ||
    normalized === 'always_interrupt'
  ) {
    return normalized;
  }
  return 'in_app_only';
}

function isAppVisibleAndFocused() {
  try {
    if (typeof document === 'undefined' || typeof window === 'undefined') return false;
    const isVisible = document.visibilityState === 'visible' && !document.hidden;
    const hasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    return Boolean(isVisible && hasFocus);
  } catch (_) {
    return false;
  }
}

/**
 * Play a short alert tone using the Web Audio API.
 * No external audio files required. Generates a two-tone beep pattern.
 * Failure is non-blocking — callers should catch errors.
 */
function playAlertTone() {
  if (typeof window === 'undefined' || !window.AudioContext && !window.webkitAudioContext) {
    return;
  }
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx();
  const now = ctx.currentTime;

  // Two short beeps: 880 Hz for 150ms, gap, then 880 Hz for 150ms
  [0, 0.25].forEach((offset) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, now + offset);
    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.15);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + offset);
    osc.stop(now + offset + 0.15);
  });

  // Close context after tones finish
  setTimeout(() => { try { ctx.close(); } catch (_) {} }, 600);
}

/**
 * Clear seen event IDs (for testing)
 */
export function clearSeenEventIds() {
  seenEventIds.clear();
  console.log('[APP-NOTIFY] cleared seen event IDs');
}

/**
 * Get count of seen event IDs (for debugging)
 */
export function getSeenEventCount() {
  return seenEventIds.size;
}
